// src/ui/features/calculator/components/ModuleCalcPanel.jsx
import React, { useMemo, useRef, useState } from "react";
import Card from "../ui/Card";

function clampNum(v, min, max) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  return Math.max(min, Math.min(max, n));
}

const DEFAULT_ROWS = [
  { weight: 70, grade: "" },
  { weight: 10, grade: "" },
  { weight: 10, grade: "" },
  { weight: 10, grade: "" },
];

export default function ModuleCalcPanel() {
  const [rows, setRows] = useState(DEFAULT_ROWS);

  // Mobile double-fire guard (touch/click)
  const actionLockRef = useRef({ addAt: 0, removeAt: 0 });
  const ACTION_LOCK_MS = 250;

  const weightSum = useMemo(
    () => rows.reduce((s, r) => s + (Number(r.weight) || 0), 0),
    [rows]
  );

  const moduleGrade = useMemo(() => {
    if (weightSum !== 100) return null;

    let total = 0;
    let hasAny = false;

    for (const r of rows) {
      const w = Number(r.weight);
      const g = Number(r.grade);
      if (!Number.isFinite(w) || w <= 0) continue;
      if (Number.isFinite(g)) {
        hasAny = true;
        total += (w / 100) * g;
      }
    }

    if (!hasAny) return null;
    return Number.isFinite(total) ? total : null;
  }, [rows, weightSum]);

  const error =
    weightSum === 100 ? "" : "Weights must add up to 100% to calculate a module grade.";

  const onAddRow = () => {
    const now = Date.now();
    if (now - actionLockRef.current.addAt < ACTION_LOCK_MS) return;
    actionLockRef.current.addAt = now;

    setRows((prev) => [...prev, { weight: 10, grade: "" }]);
  };

  const onRemoveRow = (idx) => {
    const now = Date.now();
    if (now - actionLockRef.current.removeAt < ACTION_LOCK_MS) return;
    actionLockRef.current.removeAt = now;

    setRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const onUpdate = (idx, field, value) => {
    setRows((prev) =>
      prev.map((r, i) => {
        if (i !== idx) return r;
        if (field === "weight") return { ...r, weight: clampNum(value, 0, 100) };
        if (field === "grade") return { ...r, grade: clampNum(value, 0, 100) };
        return r;
      })
    );
  };

  const onReset = () => setRows(DEFAULT_ROWS);

  return (
    <div className="space-y-4">
      <Card className="p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-50">
              Module calculator
            </h3>
            <div className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
              Enter your assessment weights and grades to calculate your final module mark.
            </div>
          </div>

          <button
            type="button"
            onClick={onReset}
            className="
              rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm font-semibold text-neutral-800
              hover:bg-neutral-100
              dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-100 dark:hover:bg-neutral-800
            "
          >
            Reset
          </button>
        </div>

        {/* DESKTOP */}
        <div className="mt-4 hidden sm:block rounded-2xl border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-950">
          <div className="grid grid-cols-[1fr_92px_150px_92px_96px] gap-x-4 gap-y-2 text-xs font-semibold text-neutral-500 dark:text-neutral-400">
            <div>Component</div>
            <div className="text-right">Weight</div>
            <div className="text-right">Grade slider</div>
            <div className="text-right">Grade %</div>
            <div className="text-right"></div>
          </div>

          <div className="mt-2 space-y-2">
            {rows.map((r, idx) => {
              const label = `C${idx + 1}`;
              const sliderValue = r.grade === "" ? 0 : Number(r.grade);

              return (
                <div
                  key={idx}
                  className="grid grid-cols-[1fr_92px_150px_92px_96px] items-center gap-x-4 gap-y-2"
                >
                  {/* Label only (no input) */}
                  <div
                    className="
                      w-full rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-1.5 text-sm font-medium text-neutral-900
                      dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-50
                    "
                    aria-label={`Component label ${label}`}
                  >
                    {label}
                  </div>

                  <input
                    inputMode="numeric"
                    value={r.weight}
                    onChange={(e) => onUpdate(idx, "weight", e.target.value)}
                    className="
                      w-full rounded-xl border border-neutral-200 bg-white px-2 py-1.5 text-sm text-right text-neutral-900
                      focus:outline-none focus:ring-2 focus:ring-blue-500/40
                      dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-50 dark:focus:ring-blue-400/40
                    "
                    placeholder="0"
                    aria-label={`Weight for ${label}`}
                  />

                  <div className="flex flex-col items-end">
                    <input
                      type="range"
                      min={0}
                      max={100}
                      step={1}
                      value={sliderValue}
                      onChange={(e) => onUpdate(idx, "grade", e.target.value)}
                      className="w-full max-w-[140px]"
                      aria-label={`Grade slider for ${label}`}
                    />

                    <div className="mt-1 flex w-full max-w-[140px] items-center justify-between text-[11px] text-neutral-600 dark:text-neutral-400">
                      <span className="tabular-nums">0</span>
                      <span className="tabular-nums font-medium text-neutral-800 dark:text-neutral-200">
                        {Number.isFinite(Number(r.grade)) ? `${Number(r.grade)}%` : "—"}
                      </span>
                      <span className="tabular-nums">100</span>
                    </div>
                  </div>

                  <input
                    inputMode="numeric"
                    value={r.grade}
                    onChange={(e) => onUpdate(idx, "grade", e.target.value)}
                    className="
                      w-full rounded-xl border border-neutral-200 bg-white px-2 py-1.5 text-sm text-right text-neutral-900
                      focus:outline-none focus:ring-2 focus:ring-blue-500/40
                      dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-50 dark:focus:ring-blue-400/40
                    "
                    placeholder="—"
                    aria-label={`Grade for ${label}`}
                  />

                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => onRemoveRow(idx)}
                      className="
                        rounded-xl px-3 py-2 text-sm font-medium transition
                        text-neutral-700 hover:bg-neutral-100
                        dark:text-neutral-200 dark:hover:bg-neutral-900
                      "
                      aria-label={`Remove ${label}`}
                      title="Remove"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-3 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={onAddRow}
              className="
                rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm font-semibold text-neutral-800
                hover:bg-neutral-100
                dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-100 dark:hover:bg-neutral-800
              "
            >
              Add component
            </button>

            <div className="text-sm text-neutral-600 dark:text-neutral-400">
              Total weight:{" "}
              <span
                className={
                  weightSum === 100
                    ? "font-semibold text-neutral-900 dark:text-neutral-50"
                    : "font-semibold text-red-700 dark:text-red-300"
                }
              >
                {weightSum}%
              </span>
            </div>
          </div>
        </div>

        {/* MOBILE */}
        <div className="mt-4 sm:hidden">
          <table className="w-full table-fixed border-separate border-spacing-y-2">
            <colgroup>
              <col className="w-[14%]"/>
              <col className="w-[20%]"/>
              <col className="w-[35%]"/>
              <col className="w-[20%]"/>
              <col className="w-[12%]"/>
            </colgroup>

            <thead>
              <tr className="text-left text-[11px] font-semibold text-neutral-600 dark:text-neutral-400">
                <th className="pl-4 pr-2 py-1">Comp</th>
                <th className="px-2 py-1">Weight</th>
                <th className="px-1 py-1">Grade</th>
                <th className="px-2 py-1">%</th>
                <th className="pr-6 py-1"></th>
              </tr>
            </thead>

            <tbody>
              {rows.map((r, idx) => {
                const label = `C${idx + 1}`;
                const sliderValue = r.grade === "" ? 0 : Number(r.grade);

                return (
                  <tr
                    key={idx}
                    className="rounded-xl border border-neutral-200 bg-white shadow-sm dark:border-neutral-800 dark:bg-neutral-950"
                  >
                    <td className="pl-4 pr-2 py-2 text-sm font-medium text-neutral-800 dark:text-neutral-100 whitespace-nowrap">
                      {label}
                    </td>

                    <td className="px-2 py-2">
                      <input
                        inputMode="numeric"
                        value={r.weight}
                        onChange={(e) => onUpdate(idx, "weight", e.target.value)}
                        className="
                          w-full max-w-[52px]
                          rounded-lg border border-neutral-200 bg-white px-1.5 py-1 text-sm
                          focus:border-neutral-400 focus:outline-none
                          dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-50
                        "
                        placeholder="0"
                        aria-label={`Weight for ${label}`}
                      />
                    </td>

                    <td className="px-1 py-2">
                      <div className="w-full">
                        <input
                          type="range"
                          min={0}
                          max={100}
                          step={1}
                          value={sliderValue}
                          onChange={(e) => onUpdate(idx, "grade", e.target.value)}
                          className="w-full h-1"
                          aria-label={`Grade slider for ${label}`}
                        />

                        <div className="mt-0.5 flex w-full items-center justify-between text-[9px] text-neutral-600 dark:text-neutral-400">
                          <span className="tabular-nums">0</span>
                          <span className="tabular-nums font-medium text-neutral-800 dark:text-neutral-200">
                            {Number.isFinite(Number(r.grade)) ? `${Number(r.grade)}%` : "—"}
                          </span>
                          <span className="tabular-nums">100</span>
                        </div>
                      </div>
                    </td>

                    <td className="px-2 py-2">
                      <input
                        inputMode="numeric"
                        value={r.grade}
                        onChange={(e) => onUpdate(idx, "grade", e.target.value)}
                        className="
                          w-full max-w-[72px]
                          rounded-lg border border-neutral-200 bg-white px-2 py-1 text-sm
                          focus:border-neutral-400 focus:outline-none
                          dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-50
                        "
                        placeholder="—"
                        aria-label={`Grade for ${label}`}
                      />
                    </td>

                    <td className="py-2 text-right pr-6">
                      <button
                        type="button"
                        onClick={() => onRemoveRow(idx)}
                        className="
                          inline-flex h-7 w-7 items-center justify-center rounded-lg transition
                          text-neutral-600 hover:bg-neutral-100
                          dark:text-neutral-200 dark:hover:bg-neutral-900
                        "
                        aria-label={`Remove ${label}`}
                        title="Remove"
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="h-4 w-4"
                          aria-hidden="true"
                        >
                          <path d="M18 6L6 18" />
                          <path d="M6 6l12 12" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="mt-2 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={onAddRow}
              className="
                rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm font-semibold text-neutral-800
                hover:bg-neutral-100
                dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-100 dark:hover:bg-neutral-800
              "
            >
              Add component
            </button>

            <div className="text-sm text-neutral-600 dark:text-neutral-400">
              Total:{" "}
              <span
                className={
                  weightSum === 100
                    ? "font-semibold text-neutral-900 dark:text-neutral-50"
                    : "font-semibold text-red-700 dark:text-red-300"
                }
              >
                {weightSum}%
              </span>
            </div>
          </div>
        </div>

        {/* Result */}
        <div className="mt-4 rounded-2xl border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-950">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold text-neutral-900 dark:text-neutral-50">
              Module result
            </div>

            <div className="text-2xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-50">
              {moduleGrade == null ? "—" : `${moduleGrade.toFixed(2)}%`}
            </div>
          </div>

          {error ? (
            <div className="mt-2 text-sm text-red-700 dark:text-red-300">{error}</div>
          ) : (
            <div className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">
              Calculated as the weighted average of the components above.
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
